import React from 'react';

import parse from 'html-react-parser';

import { loadableReady } from '@ulta/core/components/Loadable';
import AppContainer from '@ulta/core/containers/AppContainer/AppContainer';
import constants from '@ulta/core/utils/constants/constants';

import { getApolloClient } from '../apollo_client/apollo_client';
import { devLogger } from '../devMode/devMode';
import { handleEmptyObjects } from '../handleEmptyObjects/handleEmptyObjects';
import * as utils from './dom';

/**
 * Parses a string into valid HTML
 *
 * @method
 * @param { String } string - String to parse
 * @returns { String } - String as HTML
 */
export const HTMLparser = ( string = '' ) => {
  const content = parse( string ?? '' );
  return content && !!content.length ? content : null;
};

export const renderApplication = function( config ){

  // Keep this alpha sorted for easier diffs
  const {
    adoptSessionExternally,
    amityApiEndpoint,
    amityClientKey,
    faqCommunityId,
    newsCommunityId,
    termsAndConditionsUrl,
    defaultTab,
    privacyAndPolicyUrl,
    groupQueryString,
    userQueryString,
    postQueryString,
    allowUpdateDisplayName,
    amityIframeUrl,
    amityMessageOrigin,
    allowRootQuerySkip,
    applePaySessionValidationEndPoint,
    auth0apiAudience,
    auth0callback,
    auth0clientId,
    auth0login,
    auth0scope,
    auth0tenant,
    domElement,
    enablePersistentSearch,
    enableSecurityErrorPage,
    findationApiKey,
    findationTestingMode,
    googleMapsApiKey,
    graphqlDomain,
    graphqlURI,
    graphqlTimeStamp,
    inflection,
    isATG,
    isBot,
    isLocal,
    isNative,
    isStaging,
    isThirdParty,
    isPaypalSdkV2,
    href,
    origin,
    locale,
    searchParams,
    pathname,
    ModuleRoutes,
    paypalEnvironment,
    powerReviewsSdk,
    previewDate,
    recentlyViewedLimit,
    recentlyViewedForAutoSuggestionLimit,
    requestHeaders,
    renderer,
    rewardsPointsLimit,
    roktAccountId,
    salsifyClientId,
    securityErrorStatusCodes,
    serverRouteUtility,
    shoppingAgentCooldownMs,
    stagingHost,
    stagingWaitingRoomRetryCount,
    stagingWaitingRoomRetryInterval,
    targetElementId = constants.APPLICATION_ROOT,
    tealiumEnvironment = 'qa',
    typeaheadMinInputLength,
    websiteDomain,
    footerSellMyInfoLinkIcon,
    enableDynamicPromoBar
  } = handleEmptyObjects( config );

  global.__ULTA_CLIENT_APPS__ = global.__ULTA_CLIENT_APPS__ || {};

  // Prevents double loading of external header/footer because loadableReady will be called twice,
  // once from the header and once from the footer. Internally loadable ready then has 2 chains of
  // callbacks to process and it invokes this renderer 2x for the header causing session issues.
  if( global.__ULTA_CLIENT_APPS__[targetElementId] && process.env.NODE_ENV !== 'test' ){
    return;
  }

  global.__ULTA_CLIENT_APPS__[targetElementId] = true;

  // Keep this alpha sorted for easier diffs
  const appConfig = {
    adoptSessionExternally,
    amityApiEndpoint,
    amityClientKey,
    faqCommunityId,
    newsCommunityId,
    termsAndConditionsUrl,
    defaultTab,
    privacyAndPolicyUrl,
    groupQueryString,
    userQueryString,
    postQueryString,
    allowUpdateDisplayName,
    amityIframeUrl,
    amityMessageOrigin,
    allowRootQuerySkip,
    applePaySessionValidationEndPoint,
    auth0apiAudience,
    auth0callback,
    auth0clientId,
    auth0login,
    auth0scope,
    auth0tenant,
    enablePersistentSearch,
    enableSecurityErrorPage,
    findationApiKey,
    findationTestingMode,
    googleMapsApiKey,
    graphqlDomain,
    graphqlURI,
    graphqlTimeStamp,
    locale,
    isATG,
    isBot,
    isLocal,
    isNative,
    isStaging,
    isThirdParty,
    isPaypalSdkV2,
    paypalEnvironment,
    powerReviewsSdk,
    previewDate,
    recentlyViewedLimit,
    recentlyViewedForAutoSuggestionLimit,
    requestHeaders,
    rewardsPointsLimit,
    roktAccountId,
    salsifyClientId,
    securityErrorStatusCodes,
    serverRouteUtility,
    shoppingAgentCooldownMs,
    stagingHost,
    stagingWaitingRoomRetryCount,
    stagingWaitingRoomRetryInterval,
    targetElementId,
    tealiumEnvironment,
    typeaheadMinInputLength,
    websiteDomain,
    footerSellMyInfoLinkIcon,
    enableDynamicPromoBar
  };

  devLogger( { appConfig } );

  const isDevMode = process.env.NODE_ENV === 'development';

  let client = getApolloClient( {
    Apollo_State: window.__APOLLO_STATE__,
    graphqlDomain,
    graphqlURI,
    stagingHost,
    previewDate,
    isStaging,
    origin,
    isNative,
    ssrMode: true,
    connectToDevTools: isDevMode,
    locale,
    requestHeaders
  } );

  renderer(
    domElement,
    <AppContainer
      href={ href }
      searchParams={ searchParams }
      pathname={ pathname }
      client={ client }
      inflection={ inflection }
      isServer={ false }
      appConfig={ appConfig }
      locale={ locale }
    >
      <ModuleRoutes
        isNative={ isNative }
      />
    </AppContainer>,
    {
      onRecoverableError: ( error ) => {
        // eslint-disable-next-line no-console
        console.error( 'Recoverable error during hydration:', error );
      }
    }
  );
};

export const renderContainer = function( config ){
  return loadableReady( () => {
    utils.renderApplication( config );
  } );
};

/**
 * Utility method to conditionally spread props, filtering out undefined/null values.
 * Used to overcome issues with React ES6 transforms and attribute spreading.
 * Only includes properties that have truthy values or explicit false values.
 *
 * @param {SpreadPropsConditions} conditions - Object containing conditional props
 * @returns {object} Filtered object with only defined properties
 *
 * @typedef {object} SpreadPropsConditions
 * @description Object where keys are prop names and values are the conditional values
 *
 * @example
 * // Basic usage with conditional props
 * const props = spreadProps({
 *   className: isActive ? 'active' : undefined,
 *   disabled: isLoading,
 *   'aria-label': hasLabel ? 'Button label' : null,
 *   onClick: handleClick
 * });
 * // Result: { disabled: true, onClick: handleClick }
 *
 * @example
 * // Usage in component composition
 * <Button
 *   {...spreadProps({
 *     primary: isPrimary,
 *     secondary: !isPrimary && isSecondary,
 *     iconImage: showIcon ? 'CaretForward' : undefined,
 *     iconSize: showIcon ? 'm' : null
 *   })}
 * />
 *
 * @example
 * // Complex conditional spreading
 * const buttonProps = spreadProps({
 *   variant: isMobile ? 'mobile' : 'desktop',
 *   size: size || undefined,
 *   loading: isLoading,
 *   'data-testid': process.env.NODE_ENV === 'test' ? 'button' : undefined
 * });
 */
export const spreadProps = ( conditions ) => {
  let props = Object.keys( conditions ).reduce( ( acc, key ) => {
    const condition = conditions[key];
    // only return attributes if they don't evaluated to undefined or null
    if( condition !== undefined && condition !== null ){
      // eslint-disable-next-line no-param-reassign
      acc[key] = condition;
    }
    return acc;
  }, {} );

  // console.log( ' conditions ', conditions );
  // console.log( ' props ', props );
  return props;
};
